import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { RestClientService } from './rest-client.service';
import { environment } from 'environments/environment';
import Swal from 'sweetalert2';

export interface AppNotification {
  id: number;
  libelle: string;
  description: string;
  is_marked_as_read: number;
  created_at: string;
  hotel_id: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private notifications$ = new BehaviorSubject<AppNotification[]>([]);
  private destroy$ = new Subject<void>();
  private ws: WebSocket | null = null;
  private reconnectTimer: any = null;
  private destroyed = false;
  private activeSwalCount = 0;

  constructor(
    private authService: AuthService,
    private restClient: RestClientService
  ) {}

  get notifications() {
    return this.notifications$.asObservable();
  }

  get unreadCount(): number {
    return this.notifications$.value.filter(n => !n.is_marked_as_read).length;
  }

  get unreadCount$() {
    return new BehaviorSubject<number>(this.unreadCount);
  }

  init() {
    this.loadNotifications();
    this.connectWS();
  }

  loadNotifications() {
    const user = this.authService.getCurrentUser();
    if (!user?.hotel_id) return;

    const request = {
      data: { hotel_id: user.hotel_id, client_id__isnull: true },
      index: 0,
      size: 50,
    };

    this.restClient.post('notification/getByCriteria', request)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res && res['items'] && !res['hasError']) {
          this.notifications$.next(res['items']);
        }
      });
  }

  markAllAsRead() {
    const unread = this.notifications$.value.filter(n => !n.is_marked_as_read);
    if (!unread.length) return;

    const datas = unread.map(n => ({ ...n, is_marked_as_read: 1 }));
    this.restClient.post('notification/update', { datas })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const updated = this.notifications$.value.map(n => ({ ...n, is_marked_as_read: 1 }));
        this.notifications$.next(updated);
      });
  }

  markAsRead(id: number) {
    const notif = this.notifications$.value.find(n => n.id === id);
    if (!notif || notif.is_marked_as_read) return;

    this.restClient.post('notification/update', { datas: [{ ...notif, is_marked_as_read: 1 }] })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const updated = this.notifications$.value.map(n =>
          n.id === id ? { ...n, is_marked_as_read: 1 } : n
        );
        this.notifications$.next(updated);
      });
  }

  private connectWS() {
    const user = this.authService.getCurrentUser();
    if (!user?.hotel_id) return;

    const baseUrl = environment.apiUrl.replace(/^http/, 'ws').replace('/api/', '');
    const url = `${baseUrl}/ws/hotel/notifications/?hotel_id=${user.hotel_id}`;

    this.ws = new WebSocket(url);

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'hotel_notification' && data.notification) {
          const current = this.notifications$.value;
          this.notifications$.next([data.notification, ...current]);
          if (data.show_sweetalert) {
            this._showSweetAlert(data.notification, data.code_evenement);
          }
        }
      } catch {}
    };

    this.ws.onclose = () => {
      if (!this.destroyed) {
        this.reconnectTimer = setTimeout(() => this.connectWS(), 5000);
      }
    };

    this.ws.onerror = () => this.ws?.close();
  }

  private _showSweetAlert(notif: AppNotification, codeEvenement?: string) {
    const ICON_MAP: Record<string, 'info' | 'warning' | 'error' | 'success'> = {
      HEBERGEMENT_CREATE: 'info',
      RESERVATION_CREATE: 'info',
      SORTIE_CLIENT: 'warning',
      DEMANDE_REMISE: 'warning',
      DEMANDE_RISTOURNE: 'warning',
      CAISSE_OUVERTURE: 'success',
      CAISSE_FERMETURE: 'warning',
      COMMANDE_LIVRAISON: 'success',
      ECHEANCE_REGLEMENT: 'warning',
      NOUVELLE_ECRITURE: 'success',
    };
    const icon = (codeEvenement && ICON_MAP[codeEvenement]) || 'info';
    const topOffset = 16 + this.activeSwalCount * 96;
    this.activeSwalCount++;

    Swal.fire({
      title: notif.libelle,
      text: notif.description || undefined,
      icon,
      toast: true,
      position: 'top-end',
      showConfirmButton: true,
      confirmButtonText: 'OK',
      timer: 30000,
      timerProgressBar: true,
      didOpen: (popup) => {
        const container = popup.closest('.swal2-container') as HTMLElement;
        if (container) {
          container.style.top = `${topOffset}px`;
        }
        popup.addEventListener('mouseenter', () => Swal.stopTimer());
        popup.addEventListener('mouseleave', () => Swal.resumeTimer());
      },
      didClose: () => {
        this.activeSwalCount = Math.max(0, this.activeSwalCount - 1);
      },
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
  }
}
